import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Grid,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  AudioFile as AudioIcon,
  VideoFile as VideoIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
  CloudQueue as DropboxIcon,
  Computer as LocalIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { api, endpoints } from '../config/api';
import { AudioFile, VideoFile, MapFragmentImage } from '../types';
import DropboxFilePicker from '../components/DropboxFilePicker';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`media-tabpanel-${index}`}
      aria-labelledby={`media-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const MediaFiles: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [audioFiles, setAudioFiles] = useState<AudioFile[]>([]);
  const [videoFiles, setVideoFiles] = useState<VideoFile[]>([]);
  const [mapImages, setMapImages] = useState<MapFragmentImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [selectedFileType, setSelectedFileType] = useState<'audio' | 'video' | 'image'>('audio');
  const [uploading, setUploading] = useState(false);
  const [dropboxPickerOpen, setDropboxPickerOpen] = useState(false);
  const [dropboxFileType, setDropboxFileType] = useState<'audio' | 'video' | 'image'>('audio');

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const [audioRes, videoRes, mapRes] = await Promise.all([
        api.get(endpoints.audioFiles),
        api.get(endpoints.videoFiles),
        api.get(endpoints.mapFragmentImages),
      ]);

      setAudioFiles(audioRes.data.data || []);
      setVideoFiles(videoRes.data.data || []);
      setMapImages(mapRes.data.data || []);
    } catch (err) {
      setError('Impossibile recuperare i file multimediali');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files: File[], type: 'audio' | 'video' | 'image', title?: string) => {
    setUploading(true);
    
    for (const file of files) {
      try {
        let endpoint = '';
        switch (type) {
          case 'audio':
            endpoint = `${endpoints.audioFiles}/upload`;
            break;
          case 'video':
            endpoint = `${endpoints.videoFiles}/upload`;
            break;
          case 'image':
            endpoint = `${endpoints.mapFragmentImages}/upload`;
            break;
        }

        const uploadData = new FormData();
        uploadData.append('file', file);
        // Se viene fornito un titolo, usalo; altrimenti usa il nome del file senza estensione
        const fileTitle = title || file.name.replace(/\.[^/.]+$/, '');
        uploadData.append('title', fileTitle);
        uploadData.append('description', ''); // Descrizione vuota di default

        await api.post(endpoint, uploadData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } catch (err: any) {
        console.error('Upload error - Full error object:', err);
        console.error('Upload error - Response:', err.response);
        console.error('Upload error - Response data:', err.response?.data);
        console.error('Upload error - Response status:', err.response?.status);

        const errorMessage = err.response?.data?.message ||
                            err.response?.data?.error ||
                            err.message ||
                            `Failed to upload ${file.name}`;
        setError(`Upload failed: ${errorMessage}`);
        console.error('Final error message shown to user:', errorMessage);
      }
    }

    setSuccess(`Successfully uploaded ${files.length} file(s)`);
    setUploading(false);
    fetchFiles();
  };
  
  const handleDropboxSelect = () => {
    // Get current tab type
    const fileType = tabValue === 0 ? 'audio' : tabValue === 1 ? 'video' : 'image';
    setDropboxFileType(fileType);
    setDropboxPickerOpen(true);
  };

  const handleDropboxFileSelected = async (file: any) => {
    setSuccess(`Importato con successo ${file.name} da Dropbox`);
    fetchFiles();
  };

  const handleDelete = (file: any, type: 'audio' | 'video' | 'image') => {
    setSelectedFile(file);
    setSelectedFileType(type);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedFile) {
      try {
        let endpoint = '';
        switch (selectedFileType) {
          case 'audio':
            endpoint = endpoints.audioFile(selectedFile.id);
            break;
          case 'video':
            endpoint = endpoints.videoFile(selectedFile.id);
            break;
          case 'image':
            endpoint = endpoints.mapFragmentImage(selectedFile.id);
            break;
        }

        await api.delete(endpoint);
        setSuccess('File eliminato con successo');
        setDeleteDialogOpen(false);
        fetchFiles();
      } catch (err) {
        setError('Impossibile eliminare il file');
        console.error(err);
      }
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes && bytes !== 0) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const AudioDropzone = () => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: { 'audio/*': ['.mp3', '.wav', '.m4a', '.ogg'] },
      maxSize: Infinity, // No size limit
      onDrop: (files) => handleFileUpload(files, 'audio'),
    });

    return (
      <Paper
        {...getRootProps()}
        sx={{
          p: 3,
          mb: 3,
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
        }}
      >
        <input {...getInputProps()} />
        <Box display="flex" flexDirection="column" alignItems="center">
          <UploadIcon sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }} />
          <Typography>
            {isDragActive ? 'Rilascia i file audio qui' : 'Trascina i file audio, o clicca per selezionare'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Formati supportati: MP3, WAV, M4A, OGG
          </Typography>
          <Typography variant="caption" color="primary" sx={{ mt: 1 }}>
            Nota: Il nome del file (senza estensione) sarà usato come titolo
          </Typography>
        </Box>
      </Paper>
    );
  };

  const VideoDropzone = () => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: { 'video/*': ['.mp4', '.avi', '.mov', '.webm'] },
      maxSize: Infinity, // No size limit
      onDrop: (files) => handleFileUpload(files, 'video'),
    });

    return (
      <Paper
        {...getRootProps()}
        sx={{
          p: 3,
          mb: 3,
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
        }}
      >
        <input {...getInputProps()} />
        <Box display="flex" flexDirection="column" alignItems="center">
          <UploadIcon sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }} />
          <Typography>
            {isDragActive ? 'Rilascia i file video qui' : 'Trascina i file video, o clicca per selezionare'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Formati supportati: MP4, AVI, MOV, WEBM
          </Typography>
          <Typography variant="caption" color="primary" sx={{ mt: 1 }}>
            Nota: Il nome del file (senza estensione) sarà usato come titolo
          </Typography>
        </Box>
      </Paper>
    );
  };

  const ImageDropzone = () => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
      maxSize: Infinity, // No size limit
      onDrop: (files) => handleFileUpload(files, 'image'),
    });

    return (
      <Paper
        {...getRootProps()}
        sx={{
          p: 3,
          mb: 3,
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
        }}
      >
        <input {...getInputProps()} />
        <Box display="flex" flexDirection="column" alignItems="center">
          <UploadIcon sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }} />
          <Typography>
            {isDragActive ? 'Rilascia le immagini qui' : 'Trascina le immagini, o clicca per selezionare'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Formati supportati: PNG, JPG, JPEG, GIF, WEBP
          </Typography>
          <Typography variant="caption" color="primary" sx={{ mt: 1 }}>
            Nota: Il nome del file (senza estensione) sarà usato come titolo
          </Typography>
        </Box>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Caricamento file multimediali...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        File Multimediali
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab icon={<AudioIcon />} label={`Audio (${audioFiles.length})`} />
          <Tab icon={<VideoIcon />} label={`Video (${videoFiles.length})`} />
          <Tab icon={<ImageIcon />} label={`Immagini (${mapImages.length})`} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle2">Carica File Audio</Typography>
          <Button
            variant="outlined"
            startIcon={<DropboxIcon />}
            onClick={handleDropboxSelect}
            size="small"
          >
            Importa da Dropbox
          </Button>
        </Box>
        <AudioDropzone />
        {audioFiles.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="body1" align="center">
                Nessun file audio caricato.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <List>
            {audioFiles.map((file) => (
              <Paper key={file.id} sx={{ mb: 1 }}>
                <ListItem>
                  <ListItemText
                    primary={file.title || file.originalName}
                    secondary={
                      <Box display="flex" gap={1}>
                        <Chip label={formatFileSize(file.fileSize || file.size)} size="small" />
                        {file.duration && <Chip label={formatDuration(file.duration)} size="small" />}
                        {file.mimeType && <Chip label={file.mimeType.split('/')[1].toUpperCase()} size="small" />}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Scarica"
                    >
                      <DownloadIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(file, 'audio')} title="Elimina">
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Paper>
            ))}
          </List>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle2">Carica File Video</Typography>
          <Button
            variant="outlined"
            startIcon={<DropboxIcon />}
            onClick={handleDropboxSelect}
            size="small"
          >
            Importa da Dropbox
          </Button>
        </Box>
        <VideoDropzone />
        {videoFiles.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="body1" align="center">
                Nessun file video caricato.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <List>
            {videoFiles.map((file) => (
              <Paper key={file.id} sx={{ mb: 1 }}>
                <ListItem>
                  <ListItemText
                    primary={file.title || file.originalName}
                    secondary={
                      <Box display="flex" gap={1}>
                        <Chip label={formatFileSize(file.fileSize || file.size)} size="small" />
                        {file.duration && <Chip label={formatDuration(file.duration)} size="small" />}
                        {file.width && file.height && (
                          <Chip label={`${file.width}x${file.height}`} size="small" />
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Scarica"
                    >
                      <DownloadIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(file, 'video')} title="Elimina">
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Paper>
            ))}
          </List>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle2">Carica Immagini</Typography>
          <Button
            variant="outlined"
            startIcon={<DropboxIcon />}
            onClick={handleDropboxSelect}
            size="small"
          >
            Importa da Dropbox
          </Button>
        </Box>
        <ImageDropzone />
        {mapImages.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="body1" align="center">
                Nessuna immagine caricata.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {mapImages.map((file) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={file.id}>
                <Card>
                  <Box
                    component="img"
                    src={file.url}
                    alt={file.originalName}
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                    }}
                  />
                  <CardContent>
                    <Typography variant="body2" noWrap title={file.title || file.originalName}>
                      {file.title || file.originalName}
                    </Typography>
                    <Box display="flex" gap={1} mt={1}>
                      <Chip label={formatFileSize(file.fileSize || file.size)} size="small" />
                      {file.width && file.height && (
                        <Chip label={`${file.width}x${file.height}`} size="small" />
                      )}
                    </Box>
                  </CardContent>
                  <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      title="Scarica"
                    >
                      <DownloadIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(file, 'image')}
                      size="small"
                      title="Elimina"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare "{selectedFile?.title || selectedFile?.originalName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annulla</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')}>
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
      
      {/* Dropbox File Picker */}
      <DropboxFilePicker
        open={dropboxPickerOpen}
        onClose={() => setDropboxPickerOpen(false)}
        fileType={dropboxFileType}
        onFileSelect={handleDropboxFileSelected}
      />
    </Box>
  );
};

export default MediaFiles;